import { Link } from "@tanstack/react-router";
import { motion } from "framer-motion";
import { ArrowUpRight, Calendar } from "lucide-react";
import type { BlogPost } from "@/data/blog-posts";
import { formatHuDate } from "@/data/blog-posts";

interface Props {
  post: BlogPost;
  index?: number;
}

export function BlogCard({ post, index = 0 }: Props) {
  return (
    <motion.article
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-50px" }}
      transition={{ duration: 0.5, delay: index * 0.08 }}
      className="group relative flex flex-col rounded-2xl bg-surface border border-border hover:border-primary/50 hover:bg-surface-elevated transition-all duration-300 overflow-hidden"
    >
      <Link
        to="/blog/$slug"
        params={{ slug: post.slug }}
        className="flex flex-col h-full"
        aria-label={post.title}
      >
        <div className="relative aspect-[16/9] overflow-hidden bg-surface-elevated">
          <img
            src={post.image}
            alt={post.imageAlt}
            loading="lazy"
            decoding="async"
            width={1280}
            height={720}
            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
          />
          <div className="absolute top-3 left-3">
            <span className="inline-flex items-center px-3 py-1 rounded-full bg-background/80 backdrop-blur border border-border text-[10px] uppercase tracking-widest text-primary">
              {post.category}
            </span>
          </div>
        </div>

        <div className="flex flex-col flex-1 p-6 gap-3">
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <Calendar className="w-3.5 h-3.5" />
            <time dateTime={post.date}>{formatHuDate(post.date)}</time>
            <span aria-hidden>·</span>
            <span>{post.readMinutes} perc olvasás</span>
          </div>

          <h3 className="font-display text-2xl md:text-[1.65rem] leading-tight group-hover:text-primary transition-colors">
            {post.title}
          </h3>

          <p className="text-sm text-muted-foreground leading-relaxed line-clamp-3">
            {post.excerpt}
          </p>

          <div className="mt-auto pt-2 inline-flex items-center gap-2 text-sm font-semibold text-primary">
            Tovább olvasom
            <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </div>
        </div>
      </Link>
    </motion.article>
  );
}
